// Adressen des Anwendungsrahmens (ST-UX-001).
//
// KEIN FREMDPAKET, und das ist eine gemessene Entscheidung. `react-router` waere
// das fuenfte Runtime-Paket von apps/web; ADR-0019 verlangt fuer jedes einen
// benannten Eintrag in `scripts/validate-application.sh`, und die Lizenzkette
// waere erneut zu pruefen. Was es dafuer gaebe, sind verschachtelte Routen,
// Ladefunktionen und Datenaktionen -- fuer zwei flache Adressen ist das
// Ausruestung, die niemand traegt. Was hier steht, ist die History-API mit
// Abonnement, und mehr ist es nicht.
//
// Sollte der Rahmen einmal verschachtelte Bereiche brauchen, ist der Wechsel
// eine eigene Entscheidung an einer Datei -- nicht eine, die heute vorsorglich
// bezahlt wird.

import { useCallback, useEffect, useSyncExternalStore, type MouseEvent, type ReactNode } from 'react';

/**
 * Eigenes Ereignis fuer den Wechsel per `pushState`.
 *
 * Der Browser feuert `popstate` NUR bei Vor und Zurueck, nicht bei einem
 * `pushState` aus dem eigenen Code. Ohne dieses Ereignis aendert sich die
 * Adresse, und der Bildschirm bleibt stehen -- ein Fehler, der beim ersten Klick
 * auffaellt und beim Zurueckgehen nicht.
 */
export const LOCATION_EVENT = 'liaison:location';

function subscribe(onChange: () => void): () => void {
  window.addEventListener('popstate', onChange);
  window.addEventListener(LOCATION_EVENT, onChange);
  return () => {
    window.removeEventListener('popstate', onChange);
    window.removeEventListener(LOCATION_EVENT, onChange);
  };
}

/**
 * Der Serverwert fuer `useSyncExternalStore`.
 *
 * Beim Rendern ohne Fenster -- im Strukturnachweis unter `node --test` -- gibt
 * es kein `location`. Die Wurzel ist dort die richtige Antwort und keine
 * Ausnahme: ein Nachweis, der am fehlenden Browser scheitert, waere kein
 * Nachweis.
 */
function currentPath(): string {
  return typeof window === 'undefined' ? '/' : window.location.pathname;
}

export function usePath(): string {
  return useSyncExternalStore(subscribe, currentPath, () => '/');
}

function currentSearch(): string {
  return typeof window === 'undefined' ? '' : window.location.search;
}

/**
 * Ein Parameter der Adresse, etwa das Register einer Seite (`?ansicht=`).
 * Er gehoert zur Ansicht und nicht zur Suche: Suchbegriffe reisen nie in der
 * Adresse (ADR-0049).
 */
export function useAdressParameter(name: string): string | null {
  const search = useSyncExternalStore(subscribe, currentSearch, () => '');
  return new URLSearchParams(search).get(name);
}

/**
 * Setzt einen Parameter, als neuer Verlaufseintrag (Zurueck fuehrt in die
 * vorige Ansicht) oder mit `ersetzen` ohne einen.
 */
export function setzeAdressParameter(name: string, wert: string | undefined, ersetzen = false): void {
  const parameter = new URLSearchParams(window.location.search);
  if (wert === undefined) {
    parameter.delete(name);
  } else {
    parameter.set(name, wert);
  }
  const rest = parameter.toString();
  const ziel = `${window.location.pathname}${rest.length === 0 ? '' : `?${rest}`}`;
  if (ersetzen) {
    window.history.replaceState(null, '', ziel);
  } else {
    window.history.pushState(null, '', ziel);
  }
  window.dispatchEvent(new Event(LOCATION_EVENT));
}

let umgeleitetNach: string | undefined;

/**
 * Ob der Pfad gerade durch eine Umleitung entstand, nicht durch einen Wechsel
 * der Nutzerin. Einmal gelesen, gilt es nicht mehr.
 */
export function warUmleitung(pfad: string): boolean {
  const treffer = umgeleitetNach === pfad;
  umgeleitetNach = undefined;
  return treffer;
}

/**
 * Ersetzt eine alte Adresse durch ihre neue, OHNE Verlaufseintrag: Zurueck
 * fuehrt dann nicht wieder auf die alte und von dort zurueck (keine Schleife),
 * und ein Lesezeichen auf die alte haelt.
 */
export function Umleitung({ nach }: { readonly nach: string }): null {
  useEffect(() => {
    umgeleitetNach = nach;
    window.history.replaceState(null, '', `${nach}${window.location.search}`);
    window.dispatchEvent(new Event(LOCATION_EVENT));
  }, [nach]);
  return null;
}

/** Wechselt die Adresse und weckt die Abonnenten. */
export function navigate(path: string): void {
  if (window.location.pathname === path) {
    return;
  }
  window.history.pushState(null, '', path);
  window.dispatchEvent(new Event(LOCATION_EVENT));
}

/**
 * Ein Verweis, der ein ECHTER Verweis bleibt.
 *
 * `<a href>` und nicht `<button onClick>`: nur so kennt ein Screenreader ihn als
 * Link, nur so zeigt die Statusleiste das Ziel, nur so funktionieren "in neuem
 * Tab oeffnen" und "Adresse kopieren". Genau diese Wege bleiben deshalb dem
 * Browser ueberlassen -- Mittelklick, Strg/Cmd, Umschalt und Alt werden nicht
 * abgefangen. Ein Rahmen, der den Mittelklick schluckt, ist fuer jemanden, der
 * mit Tabs arbeitet, kaputt, und niemand meldet es als Fehler.
 */
export function AppLink({
  to,
  children,
  className,
  ariaCurrent,
}: {
  readonly to: string;
  readonly children: ReactNode;
  readonly className?: string | undefined;
  /** 'page' fuer den Bildschirm selbst, 'true' fuer den Bereich, zu dem eine Unterseite gehoert. */
  readonly ariaCurrent?: 'page' | 'true' | undefined;
}): ReactNode {
  const onClick = useCallback(
    (event: MouseEvent<HTMLAnchorElement>): void => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
      }
      event.preventDefault();
      navigate(to);
    },
    [to],
  );
  return (
    <a href={to} onClick={onClick} className={className} aria-current={ariaCurrent}>
      {children}
    </a>
  );
}
